import { ref, reactive } from 'vue'

// Global toast state (can be used across the application)
const toasts = ref([])
let nextId = 1

// Default durations for different toast types
const defaultDurations = {
  success: 4000,
  danger: 6000,
  primary: 4000,
  secondary: 4000
}

export const useToast = () => {
  const addToast = (toast) => {
    const id = `toast-${nextId++}`
    const duration = toast.duration ?? defaultDurations[toast.type] ?? 4000
    
    const newToast = {
      id,
      type: toast.type,
      title: toast.title,
      message: toast.message,
      duration,
      createdAt: Date.now(),
      persistent: toast.persistent ?? false
    }

    toasts.value.push(newToast)

    // Auto-remove toast after duration (unless persistent)
    if (duration > 0 && !newToast.persistent) {
      setTimeout(() => {
        removeToast(id)
      }, duration)
    }

    return id
  }

  const removeToast = (id) => {
    const index = toasts.value.findIndex(toast => toast.id === id)
    if (index > -1) {
      toasts.value.splice(index, 1)
    }
  }

  const clearAll = () => {
    toasts.value = []
  }

  // Convenience methods for different toast types
  const success = (message, options = {}) => {
    return addToast({
      type: 'success',
      message,
      ...options
    })
  }

  const danger = (message, options = {}) => {
    return addToast({
      type: 'danger',
      message,
      ...options
    })
  }

  const primary = (message, options = {}) => {
    return addToast({
      type: 'primary',
      message,
      ...options
    })
  }

  const secondary = (message, options = {}) => {
    return addToast({
      type: 'secondary',
      message,
      ...options
    })
  }

  // Enhanced method for custom toasts
  const show = (options) => {
    return addToast({
      type: 'primary',
      ...options
    })
  }

  return reactive({
    toasts: toasts.value,
    addToast,
    removeToast,
    clearAll,
    success,
    danger,
    primary,
    secondary,
    show
  })
}

// Named export for direct usage without composable
export const toast = {
  success: (message, options = {}) => useToast().success(message, options),
  danger: (message, options = {}) => useToast().danger(message, options),
  primary: (message, options = {}) => useToast().primary(message, options),  
  secondary: (message, options = {}) => useToast().secondary(message, options),
  show: (options) => useToast().show(options),
  remove: (id) => useToast().removeToast(id),
  clear: () => useToast().clearAll()
}